import { useEffect, useRef, useState } from 'react';
import { useCrew } from './net.js';
import { armAudio, sfx } from './sound.js';
import { GameTable } from './components/GameTable.js';
import { Home } from './components/Home.js';
import { Lobby } from './components/Lobby.js';
import { Manual } from './components/Manual.js';
import { Feedback } from './components/Feedback.js';
import { SidePanel } from './components/SidePanel.js';
import { AmbientDrifters } from './components/Art.js';

export function App() {
  const crew = useCrew();
  const [manualOpen, setManualOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(sfx.enabled);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const manualTrigger = useRef<HTMLButtonElement | null>(null);
  const feedbackTrigger = useRef<HTMLButtonElement | null>(null);
  const { snapshot, connection, error } = crew;

  useEffect(armAudio, []);

  const closeManual = () => {
    setManualOpen(false);
    manualTrigger.current?.focus();
  };

  const closeFeedback = () => {
    setFeedbackOpen(false);
    feedbackTrigger.current?.focus();
  };

  return (
    <div className="app">
      <div className="starfield" aria-hidden="true" />
      <AmbientDrifters />

      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="topbar">
        <span className="wordmark">
          CREW<span>: QUEST FOR PLANET NINE</span>
        </span>

        <span className={`status status--${connection}`}>
          <span className="status__dot" />
          {connection === 'online'
            ? 'linked'
            : connection === 'connecting'
              ? 'linking'
              : 'signal lost'}
        </span>

        {snapshot ? <span className="topbar__room">ROOM {snapshot.room.code}</span> : null}

        <button
          className="btn btn--tiny btn--ghost"
          aria-pressed={soundOn}
          title={soundOn ? 'Mute sound effects' : 'Unmute sound effects'}
          onClick={() => {
            const next = !soundOn;
            sfx.setEnabled(next);
            setSoundOn(next);
            if (next) sfx.play('click');
          }}
        >
          {soundOn ? '\u266A Sound on' : '\u266A Muted'}
        </button>

        <button
          className="btn btn--tiny btn--ghost"
          ref={feedbackTrigger}
          onClick={() => setFeedbackOpen(true)}
        >
          Feedback
        </button>

        <button
          className="btn btn--tiny btn--ghost"
          ref={manualTrigger}
          onClick={() => setManualOpen(true)}
        >
          Manual
        </button>
        {snapshot ? (
          snapshot.game && snapshot.game.phase !== 'won' && snapshot.game.phase !== 'lost' ? (
            <button className="btn btn--tiny btn--ghost" onClick={() => setConfirmLeave(true)}>
              Quit mission
            </button>
          ) : (
            <button className="btn btn--tiny btn--ghost" onClick={() => void crew.leaveRoom()}>
              Leave
            </button>
          )
        ) : null}
      </header>

      {confirmLeave ? (
        <div className="overlay" role="alertdialog" aria-label="Quit mission">
          <div className="overlay__card overlay__card--compact">
            <h2>Quit this mission?</h2>
            <p>
              The mission ends for the whole crew and everyone returns to the lobby. Anyone who is
              offline gets dropped so you can start again straight away.
            </p>
            <div className="overlay__actions">
              <button className="btn btn--ghost" onClick={() => setConfirmLeave(false)}>
                Keep playing
              </button>
              <button
                className="btn btn--danger"
                onClick={() => {
                  setConfirmLeave(false);
                  void crew.abort();
                }}
              >
                Quit mission
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="alert alert--error" role="alert">
          <span>{error}</span>
          <button className="btn btn--tiny btn--ghost" onClick={crew.clearError}>
            Dismiss
          </button>
        </div>
      ) : null}

      {!snapshot ? (
        <main id="main" className="layout layout--solo">
          <Home crew={crew} onOpenManual={() => setManualOpen(true)} />
        </main>
      ) : (
        <main id="main" className={snapshot.game ? 'layout layout--game' : 'layout'}>
          {snapshot.game ? (
            <style>{snapshot.game.tasks.map((task, index) => {
              const owner = snapshot.room.players.find((p) => p.id === task.ownerId);
              return owner
                ? `.layout--game .tasks__list > li:nth-child(${index + 1}) { --task-owner: var(--pilot-${owner.avatarColor}); --task-claimed: "✓"; }`
                : '';
            }).join('\n')}</style>
          ) : null}
          {snapshot.game ? (
            <GameTable
              key={`${snapshot.room.code}:${snapshot.game.missionId}:${snapshot.game.attempt}`}
              crew={crew}
              snapshot={snapshot}
            />
          ) : (
            <Lobby crew={crew} snapshot={snapshot} onOpenManual={() => setManualOpen(true)} />
          )}
          <SidePanel crew={crew} snapshot={snapshot} />
        </main>
      )}

      {manualOpen ? <Manual onClose={closeManual} /> : null}

      {feedbackOpen ? (
        <Feedback
          crew={crew}
          onClose={closeFeedback}
          context={{
            screen: !snapshot ? 'home' : snapshot.game ? 'table' : 'lobby',
            ...(snapshot?.game ? { missionId: snapshot.game.missionId, phase: snapshot.game.phase } : {})
          }}
        />
      ) : null}
    </div>
  );
}
